/** Formatting helpers shared by every screen. */

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral' | 'brand'> = {
  CONNECTED: 'ok',
  DELIVERED: 'ok',
  READ: 'ok',
  SENT: 'ok',
  ACTIVE: 'ok',
  PAID: 'ok',
  RESOLVED: 'ok',
  DEGRADED: 'warn',
  CONNECTING: 'warn',
  RECONNECTING: 'warn',
  WAITING_QR: 'warn',
  QUEUED: 'warn',
  PROCESSING: 'warn',
  RETRYING: 'warn',
  PENDING: 'warn',
  PAST_DUE: 'warn',
  GRACE_PERIOD: 'warn',
  TRIALING: 'brand',
  PAUSED: 'neutral',
  DISCONNECTED: 'neutral',
  CREATING: 'neutral',
  CREATED: 'neutral',
  DELETING: 'neutral',
  DELETED: 'neutral',
  OPEN: 'brand',
  ERROR: 'danger',
  FAILED: 'danger',
  EXPIRED: 'danger',
  BLOCKED: 'danger',
  LOGGED_OUT: 'danger',
  DEAD: 'danger',
  DISABLED: 'danger',
  RESTRICTED: 'danger',
  SUSPENDED: 'danger',
  UNCOLLECTIBLE: 'danger',
};

export function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' | 'brand' {
  return STATUS_TONE[status.toUpperCase()] ?? 'neutral';
}

/** Human status label, so the console never shows raw SCREAMING_SNAKE to a customer. */
const STATUS_LABEL: Record<string, string> = {
  CREATING: 'Criando',
  DISCONNECTED: 'Desconectada',
  WAITING_QR: 'Aguardando QR',
  CONNECTING: 'Conectando',
  CONNECTED: 'Conectada',
  DEGRADED: 'Instável',
  RECONNECTING: 'Reconectando',
  LOGGED_OUT: 'Desconectada no aparelho',
  BLOCKED: 'Bloqueada',
  ERROR: 'Erro',
  DELETING: 'Removendo',
  DELETED: 'Removida',
  // Assinatura (PRD §128, §129)
  TRIALING: 'Em teste',
  ACTIVE: 'Ativa',
  PAST_DUE: 'Pagamento pendente',
  GRACE_PERIOD: 'Em carência',
  RESTRICTED: 'Restrita',
  SUSPENDED: 'Suspensa',
  CANCELED: 'Cancelada',
};

/** Vocabulário próprio de cobrança — `FAILED` numa mensagem não é `FAILED` num pagamento. */
export const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aguardando pagamento',
  SUCCEEDED: 'Pago',
  FAILED: 'Não pago',
  REFUNDED: 'Estornado',
};

export const INVOICE_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  OPEN: 'Em aberto',
  PAID: 'Paga',
  VOID: 'Cancelada',
  UNCOLLECTIBLE: 'Não cobrável',
};

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: 'Pix',
  BOLETO: 'Boleto',
  CARD: 'Cartão de crédito',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** Dias inteiros até uma data; negativo quando já passou. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour12: false });
}

/** Relative time — "há 24 segundos" is what §26 asks the instance header to show. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 5) return 'agora';
  if (seconds < 60) return `há ${seconds} segundos`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  return `há ${Math.round(hours / 24)} dias`;
}

export function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

export function formatCurrency(cents: number, currency = 'BRL'): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency });
}

export function formatPhone(raw: string | null): string {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  return raw;
}

/** Colours the HTTP status in the log explorer without needing a legend. */
export function statusCodeTone(code: number): 'ok' | 'warn' | 'danger' {
  if (code >= 500) return 'danger';
  if (code >= 400) return 'warn';
  return 'ok';
}
